import { supabase } from '../supabaseClient';
import { Question } from '../types/models';

// Typ danych, jaki przyjmuje funkcja tworząca pytanie (bez 'id' i z nową strukturą)
// Nadal go eksportujemy, bo przyda się w formularzu
export type CreateQuestionData = {
  text: string;
  optionA: string;
  optionB: string;
  optionC: string;
  correct_options: Array<'A' | 'B' | 'C'>;
  imageFile?: File | null; // Opcjonalny plik do wysłania
};

export const questionService = {
  // Funkcja pobierająca wszystkie pytania
  getAllQuestions: async (): Promise<Question[]> => {
    const { data, error } = await supabase.from('questions').select('*');

    if (error) {
      console.error('Błąd podczas pobierania pytań:', error);
      throw new Error(error.message);
    }
    
    // Supabase zwraca dane w formacie bazy danych, musimy je zmapować
    // na nasz wewnętrzny typ Question.
    return data.map(q => ({
        id: q.id,
        text: q.text,
        optionA: q.optionA,
        optionB: q.optionB,
        optionC: q.optionC,
        // Zabezpieczenie na wypadek, gdyby correct_options było null
        correctOptions: q.correct_options || [], 
        imageUrl: q.image_url,
    }));
  },

  // Funkcja dodająca nowe pytanie
  addQuestion: async (data: CreateQuestionData): Promise<Question> => {
    let imageUrl: string | null = null;

    // 1. Jeśli jest plik, najpierw wyślij go do Supabase Storage
    if (data.imageFile) {
      const file = data.imageFile;
      const filePath = `question-images/${Date.now()}_${file.name}`;
      
      const { error: uploadError } = await supabase.storage
        .from('question_images') // Nazwa naszego "wiadra" (bucket)
        .upload(filePath, file);

      if (uploadError) {
        console.error('Błąd podczas wysyłania obrazka:', uploadError);
        throw new Error(uploadError.message);
      }
      
      // 2. Pobierz publiczny URL do zapisu w bazie
      const { data: { publicUrl } } = supabase.storage
        .from('question_images')
        .getPublicUrl(filePath);
        
      imageUrl = publicUrl;
    }

    // 3. Zapisz dane pytania (wraz z URL-em obrazka) w bazie danych
    const { data: newQuestionData, error: insertError } = await supabase
      .from('questions')
      .insert({
        text: data.text,
        optionA: data.optionA,
        optionB: data.optionB,
        optionC: data.optionC,
        correct_options: data.correct_options,
        image_url: imageUrl,
      })
      .select() // .select() sprawia, że Supabase zwróci wstawiony wiersz
      .single(); // .single() oczekuje jednego wyniku

    if (insertError) {
      console.error('Błąd podczas dodawania pytania:', insertError);
      throw new Error(insertError.message);
    }
    
    // Zmapuj zwrócone dane na nasz typ
    return {
        id: newQuestionData.id,
        text: newQuestionData.text,
        optionA: newQuestionData.optionA,
        optionB: newQuestionData.optionB,
        optionC: newQuestionData.optionC,
        correctOptions: newQuestionData.correct_options || [],
        imageUrl: newQuestionData.image_url,
    };
  },
  // Funkcja aktualizująca pytanie
  updateQuestion: async (id: string, data: Partial<CreateQuestionData>): Promise<Question> => {
    let imageUrl: string | null | undefined = undefined;
    // Jeśli jest nowy plik obrazu, wyślij go do Supabase Storage
    if (data.imageFile) {
      const file = data.imageFile;
      const filePath = `question-images/${Date.now()}_${file.name}`;
      const { error: uploadError } = await supabase.storage
        .from('question_images')
        .upload(filePath, file);
      if (uploadError) {
        console.error('Błąd podczas wysyłania obrazka:', uploadError);
        throw new Error(uploadError.message);
      }
      const { data: { publicUrl } } = supabase.storage
        .from('question_images')
        .getPublicUrl(filePath);
      imageUrl = publicUrl;
    }

    // Przygotuj dane do aktualizacji
    const updateData: any = { ...data };
    if (imageUrl !== undefined) updateData.image_url = imageUrl;
    if (updateData.imageFile !== undefined) delete updateData.imageFile;
    if (updateData.correct_options) updateData.correct_options = updateData.correct_options;

    const { data: updated, error } = await supabase
      .from('questions')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();
    if (error) {
      console.error('Błąd podczas aktualizacji pytania:', error);
      throw new Error(error.message);
    }
    return {
      id: updated.id,
      text: updated.text,
      optionA: updated.optionA,
      optionB: updated.optionB,
      optionC: updated.optionC,
      correctOptions: updated.correct_options || [],
      imageUrl: updated.image_url,
    };
  },
};